import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import GuestLayout from '../components/GuestLayout';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, Plus, Minus, Loader2, Check, Package, Truck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { token } = useAuth();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [shippingOption, setShippingOption] = useState('take');
  const [loading, setLoading] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(res => setProduct(res.data)).catch(console.error);
  }, [id]);

  const handleAddToCart = async () => {
    if (!token) { navigate('/login'); return; }
    setLoading(true);
    const success = await addToCart('product', product.id, quantity, {
      shipping_option: product.shipping_available ? shippingOption : 'take'
    });
    setLoading(false);
    if (success) {
      setAdded(true);
      toast.success('Added to cart');
      setTimeout(() => setAdded(false), 2000);
    }
  };

  if (!product) return (
    <GuestLayout>
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-espresso border-t-transparent rounded-full animate-spin" />
      </div>
    </GuestLayout>
  );

  return (
    <GuestLayout>
      <div className="max-w-md mx-auto md:max-w-2xl">
        <div className="relative aspect-square overflow-hidden">
          <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
          <button
            data-testid="product-back-btn"
            onClick={() => navigate(-1)}
            className="absolute top-5 left-5 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center"
          >
            <ArrowLeft size={18} className="text-espresso" />
          </button>
          {product.inventory <= 2 && (
            <span className="absolute top-5 right-5 bg-warmgold text-espresso text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
              Only {product.inventory} left
            </span>
          )}
        </div>

        <div className="px-6 py-6">
          <span className="inline-block text-xs uppercase tracking-widest text-text-muted font-semibold bg-sand px-3 py-1 rounded-full mb-3">
            {product.category}
          </span>
          <div className="flex items-start justify-between mb-4">
            <h1 data-testid="product-title" className="font-heading text-2xl text-espresso">{product.title}</h1>
            <span className="text-espresso font-semibold text-xl">${product.price.toFixed(2)}</span>
          </div>
          <p className="text-text-muted leading-relaxed mb-6">{product.description}</p>

          {/* Shipping options */}
          {product.shipping_available && (
            <div className="mb-6">
              <p className="text-xs uppercase tracking-widest text-text-muted font-semibold mb-3">Fulfillment</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="shipping-take"
                  onClick={() => setShippingOption('take')}
                  className={`flex items-center gap-2 p-4 rounded-xl border transition-colors ${
                    shippingOption === 'take' ? 'border-espresso bg-sand' : 'border-warm-border'
                  }`}
                >
                  <Package size={18} className="text-espresso" />
                  <span className="text-sm font-medium text-espresso">Take with me</span>
                </button>
                <button
                  data-testid="shipping-ship"
                  onClick={() => setShippingOption('ship')}
                  className={`flex items-center gap-2 p-4 rounded-xl border transition-colors ${
                    shippingOption === 'ship' ? 'border-espresso bg-sand' : 'border-warm-border'
                  }`}
                >
                  <Truck size={18} className="text-espresso" />
                  <span className="text-sm font-medium text-espresso">Ship to home</span>
                </button>
              </div>
            </div>
          )}

          {/* Quantity */}
          <div className="flex items-center gap-4 mb-8">
            <span className="text-xs uppercase tracking-widest text-text-muted font-semibold">Quantity</span>
            <div className="flex items-center gap-3 bg-sand rounded-full px-1 py-1">
              <button
                data-testid="product-qty-minus"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-espresso"
              >
                <Minus size={14} />
              </button>
              <span data-testid="product-qty-value" className="text-espresso font-semibold w-6 text-center">{quantity}</span>
              <button
                data-testid="product-qty-plus"
                onClick={() => setQuantity(Math.min(product.inventory, quantity + 1))}
                className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-espresso"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>

          <Button
            data-testid="product-add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading || added || product.inventory === 0}
            className="w-full bg-espresso hover:bg-espresso-light text-white rounded-full h-14 text-base font-medium transition-transform active:scale-95"
          >
            {loading ? <Loader2 className="animate-spin mr-2" size={18} /> : added ? <Check className="mr-2" size={18} /> : null}
            {added ? 'Added' : product.inventory === 0 ? 'Sold Out' : `Add to Cart - $${(product.price * quantity).toFixed(2)}`}
          </Button>
        </div>
      </div>
    </GuestLayout>
  );
}
